import React, {useEffect, useState} from "react";
import {useAuthInfo} from "@propelauth/react";
import {Download, X} from "lucide-react";
import {fetchWithToken} from "../../api/apiBase";
import {FileView} from "../files/files";
import {downloadFile, FileViewer} from "../files/FileViewer";
import {Button} from "../ui/button";
import {Spinner} from "../ui/spinner";


type ReviewDocumentsProps = {
    project_id: string;
    bidFolderId?: string;           // bid.root.id
    scopeId?: string | null;        // its root folder holds the scope documents
    onClose: () => void;
}

type Docs = {
    bid: FileView[];
    scope: FileView[];
}

/**
 * The AI review popup's side pane - the bid's and the scope's documents, one at a time, so the developer can
 * check the review against them. Each folder is listed one deep, like the files cards (the review reads every
 * subfolder too).
 * */
export function ReviewDocuments({project_id, bidFolderId, scopeId, onClose}: ReviewDocumentsProps) {
    const {accessToken} = useAuthInfo();
    const [docs, setDocs] = useState<Docs | null>(null);
    const [fileId, setFileId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // files only - no folders or links
    const listFolder = (folder_id?: string): Promise<FileView[]> => !folder_id
        ? Promise.resolve([])
        : fetchWithToken(`/files/list/v2/${folder_id}`, accessToken as string)
            .then((files: FileView[]) => files.filter((f) => !f.is_folder && !f.is_link));

    useEffect(() => {
        const scopeRoot: Promise<string | undefined> = scopeId
            ? fetchWithToken(`/projects/${project_id}/scope/${scopeId}`, accessToken as string).then((s) => s.root?.id)
            : Promise.resolve(undefined);
        Promise.all([listFolder(bidFolderId), scopeRoot.then(listFolder)])
            .then(([bid, scope]) => {
                setDocs({bid, scope});
                // the proposal first
                const first = bid[0] ?? scope[0];
                if (first) setFileId(first.id);
            })
            .catch((err) => setError(String(err?.message ?? err)));
    }, [bidFolderId, scopeId]);

    const all = docs ? docs.bid.concat(docs.scope) : [];
    const file = all.find((f) => f.id === fileId);

    return <div className="flex flex-col h-full min-h-0 gap-2">
        <div className="flex items-center gap-1">
            <select className="h-8 flex-1 min-w-0 rounded-md border border-input px-2 text-sm"
                    value={fileId ?? ""} disabled={all.length === 0}
                    onChange={(e) => setFileId(e.target.value)}>
                {docs && docs.bid.length > 0 &&
                    <optgroup label="Bid documents">
                        {docs.bid.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </optgroup>}
                {docs && docs.scope.length > 0 &&
                    <optgroup label="Scope documents">
                        {docs.scope.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </optgroup>}
            </select>
            {file &&
                <Button variant="ghost" size="icon" type="button" className="h-8 w-8" title="Download"
                        onClick={() => downloadFile(project_id, file.id, accessToken as string)
                            .catch((err) => setError(String(err?.message ?? err)))}>
                    <Download className="w-4 h-4"/>
                </Button>}
            <Button variant="ghost" size="icon" type="button" className="h-8 w-8" title="Close" onClick={onClose}>
                <X className="w-4 h-4"/>
            </Button>
        </div>

        {error && <div className="text-sm text-destructive">{error}</div>}
        {!docs && !error && <Spinner/>}
        {docs && all.length === 0 && <div className="text-sm text-muted-foreground">No documents on this bid or its scope.</div>}
        {file &&
            <div className="flex-1 min-h-0">
                <FileViewer project_id={project_id} file={file}/>
            </div>}
    </div>
}
